// Helpers that classify open follow-ups by urgency for the mode-level
// urgency indicator (D1). Sibling to `followUpAttention.ts`, which only
// flips between "needs attention" (overdue or today) and "doesn't."
//
// This helper adds a gradient (overdue > due_soon > due_today > none) so the
// Sales Console mode badge can pick a KPI tone instead of a boolean.

import type { Lead } from "@/types/lead";

export type FollowUpUrgencyTier = "overdue" | "due_soon" | "due_today" | "none";

export type FollowUpUrgencySummary = {
  tier: FollowUpUrgencyTier;
  /** Number of open follow-ups that fall into `tier` (not the total across all tiers). */
  count: number;
};

const TIER_RANK: Record<FollowUpUrgencyTier, number> = {
  none: 0,
  due_today: 1,
  due_soon: 2,
  overdue: 3,
};

function classifyFollowUp(dueIso: string, nowMs: number, dueSoonMs: number): FollowUpUrgencyTier {
  const due = Date.parse(dueIso);
  if (Number.isNaN(due)) return "none";
  const delta = due - nowMs;
  if (delta < 0) return "overdue";
  if (delta <= dueSoonMs) return "due_soon";
  const dueDate = new Date(due);
  const now = new Date(nowMs);
  if (
    dueDate.getFullYear() === now.getFullYear() &&
    dueDate.getMonth() === now.getMonth() &&
    dueDate.getDate() === now.getDate()
  ) {
    return "due_today";
  }
  return "none";
}

/**
 * Walk every open follow-up across the supplied leads, classify each into a
 * tier, and return the worst tier seen plus the count of follow-ups in that
 * worst tier.
 *
 * Closed/cancelled follow-ups and `isTest` leads are ignored (matches the
 * `countFollowUpsNeedingAttention` convention).
 */
export function resolveLeadsFollowUpUrgency(
  leads: Lead[],
  nowMs: number,
  dueSoonMs: number,
): FollowUpUrgencySummary {
  let worst: FollowUpUrgencyTier = "none";
  let worstCount = 0;
  for (const lead of leads) {
    if (lead.isTest) continue;
    for (const f of lead.follow_ups ?? []) {
      if (f.status !== "open") continue;
      const tier = classifyFollowUp(f.due_date, nowMs, dueSoonMs);
      if (tier === "none") continue;
      if (TIER_RANK[tier] > TIER_RANK[worst]) {
        worst = tier;
        worstCount = 1;
      } else if (tier === worst) {
        worstCount += 1;
      }
    }
  }
  return { tier: worst, count: worstCount };
}
